import { CurrencyPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import {
  BehaviorSubject,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { environment } from '@app/env-service';
import { GetPickingItemsResponse } from '@app/interfaces/purchase-order.model';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { IOrderSalesPicklistPreview } from 'v2/modules/order/interfaces/order-picklist-preview';
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';

@Injectable({
  providedIn: 'root',
})
export class PoService {
  url :string = environment.url;
  urlV2 :string = environment.urlV2;
  curPipe :CurrencyPipe;
  decimalPlacesPipe :DecimalPlacesPipe;

  // 1 active 20 production-started 30 production-active 40 production completed 50 in-transit 88 delivered 99 completed
  public orderStatus :any[] = [
    { id: 1, text: 'active' },
    { id: 20, text: 'production started' },
    // { id: 30, text: 'estimated arrival' },
    { id: 40, text: 'production completed' },
    { id: 50, text: 'in-transit' },
    // { id: 88, text: 'delivered' },
    { id: 99, text: 'completed' },
  ];

  public paymentTypes :any[] = [
    { id: 1, text: 'wire transfer' },
    { id: 20, text: 'cash' },
    { id: 30, text: 'ach' },
    { id: 40, text: 'credit card' },
    { id: 50, text: 'debit card' },
    { id: 60, text: 'check' },
  ];

  private readonly _purchaseOrders = new BehaviorSubject<any>(<any>[]);
  public purchaseOrders$ = this._purchaseOrders.asObservable();

  private readonly _activePoShippingCosts = new BehaviorSubject<any>(<any>[]);
  public activePoShippingCosts$ = this._activePoShippingCosts.asObservable();

  private readonly _poItems = new BehaviorSubject<any>(<any>[]);
  private readonly _poManItems = new BehaviorSubject<any>(<any>[]);
  public poItems$ = this._poItems.asObservable();
  public poManItems$ = this._poManItems.asObservable();

  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _exactPurchaseOrders = new BehaviorSubject<any[]>(<any[]>[]);
  private readonly _similarPurchaseOrders = new BehaviorSubject<any[]>(<any[]>[]);
  private readonly _pickingItemsFromOrderIds =
    new BehaviorSubject<IOrderSalesPicklistPreview>(<IOrderSalesPicklistPreview>{});
  private readonly _loadingPicklistItems = new BehaviorSubject<boolean>(
    <boolean>false,
  );

  loading$ = this._loading.asObservable();
  exactPurchaseOrders$ = this._exactPurchaseOrders.asObservable();
  similarPurchaseOrders$ = this._similarPurchaseOrders.asObservable();
  pickingItemsFromOrderIds$ = this._pickingItemsFromOrderIds.asObservable();
  loadingPicklistItems$ = this._loadingPicklistItems.asObservable();

  public gettingRecords :boolean = false;

  public totalCost :number = 0;
  public totalDownpayment :number = 0;
  public itemCount :number = 0;
  public totalQuantity :number = 0;

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';
  private onDestroy$ = new Subject();

  constructor(
    private translate :TranslateService,
    private http :HttpClient,
    private featureToggleService :FeatureToggleService,
  ) {
    // this.get({ page: 1, per_page: 20 })
    this.curPipe = new CurrencyPipe('en');
    this.decimalPlacesPipe = new DecimalPlacesPipe();
  }

  getPoNumberFromPOString(poStr :string) {
    let poNumber :number = 0;

    try {
      poNumber = parseInt(poStr.replace(/^\D+/g, ''), 10);
    } catch (e) {}

    return poNumber ? poNumber : -1;
  }

  getByPONumber(
    poNumber :string,
    exact ?:boolean,
    relations :string = '',
  ) :Observable<any> {
    const params :any = {
      rrSearchPoKeyword: poNumber,
    };
    if (exact) {
      params.exact = 1;
    }
    if (relations.length > 0) {
      params.relations = relations;
    }

    return this.http.get(`${this.url}/mt_purchase_orders`, {
      params: params,
    });
  }

  getAsync(params) :Observable<any> {
    return this.http.get(`${this.url}/mt_purchase_orders`, {
      params: params,
    });
  }

  search(params) :Observable<any> {
    return this.http.get(`${this.url}/mt_purchase_orders/search`, {
      params: params,
    });
  }

  // po list
  get(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/mt_purchase_orders`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        if (resp.data) {
          resp.data.forEach((row :any) => {
            row.items_summary = this.getItemsTexts(row);

            // check if in-transit
            if (row.order_status_id == 50) {
              const transitStatus :any = row.purchase_order_status.find(
                (stat :any) => stat.order_status_id == 50,
              );
              if (transitStatus) {
                try {
                  transitStatus.json = JSON.parse(transitStatus.json);
                } catch (e) {}
                row.transitStatus = transitStatus;
              }
            }
          });
          this._purchaseOrders.next(resp);
        }
      });
  }

  getItemsTexts(purchaseOrder :any) {
    const mergedArray = [
      ...purchaseOrder.purchase_order_items,
      ...(purchaseOrder.purchase_order_custom_items || []),
    ];
    const items = mergedArray
      .map(
        (item) =>
          (item.internal_sku &&
          item.internal_sku != 'N/A' &&
          item.internal_sku != 'null'
            ? '[' + item.internal_sku + '] '
            : '') +
          item.product_name +
          ' (x' +
          this.decimalPlacesPipe.transform(item.quantity) +
          ' ' +
          (item?.ordered_unit_of_measure?.name || '') +
          ')',
      )
      .join(', ');

    return items;
  }

  getItemsTextsToRowsArray(purchaseOrder :any) {
    const items = purchaseOrder.purchase_order_items.map((item) => {
      const receivedQty :number = item.received_entries
        ? item.received_entries.quantity
        : 0;
      const landedCost :number =
        item.unit_price +
        (((item.unit_price * item.quantity) / purchaseOrder.total_cost) *
          purchaseOrder.total_shipping_cost) /
          item.quantity;

      const parsedItem :any =
        (item.internal_sku &&
        item.internal_sku != 'N/A' &&
        item.internal_sku != 'null'
          ? '(' + item.internal_sku + ') '
          : '') +
        item.product_name +
        ' [' +
        receivedQty +
        '/' +
        item.quantity +
        ', ' +
        this.curPipe.transform(landedCost) +
        '/' +
        this.curPipe.transform(item.unit_price) +
        ']';

      return parsedItem;
    });

    return items;
  }

  getPurchaseOrderByIdAsync(purchaseOrderId :number) :Observable<any> {
    // const purchaseOrders: any = this._purchaseOrders.getValue();
    // if (purchaseOrders.data) {
    // 	const purchaseOrder: any = purchaseOrders.data.find(
    // 		(row) => row.id == purchaseOrderId
    // 	);
    // 	if (purchaseOrder) {
    // 		return of(purchaseOrder);
    // 	}
    // }
    return this.http.get(`${this.url}/mt_purchase_orders/${purchaseOrderId}`, {});
  }

  // form po
  updateOrder(purchaseOrderId :number, orderData :any) :Observable<any> {
    return this.http
      .post(`${this.url}/mt_purchase_orders/${purchaseOrderId}`, orderData)
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  createOrder(orderData :any) :Observable<any> {
    return this.http.post(`${this.url}/mt_purchase_orders`, orderData).pipe(
      tap((resp :any) => {
        setTimeout(() => {
          resp.items_summary = this.getItemsTexts(resp);
          const purchaseOrders :any = this._purchaseOrders.getValue();
          if (purchaseOrders && purchaseOrders.data) {
            purchaseOrders.data.splice(0, 0, resp);
            this._purchaseOrders.next(purchaseOrders);
          }
        });
        this.reset();
      }),
    );
  }

  deleteOrder(purchaseOrderId :number) {
    return this.http
      .delete(`${this.url}/mt_purchase_orders/${purchaseOrderId}`)
      .pipe(
        tap((resp :any) => {
          this.removePurchaseOrderRow(purchaseOrderId);
          // const purchaseOrders: any = this._purchaseOrders.getValue();
          // const poIdx = purchaseOrders.data.findIndex(
          // 	(row) => row.id == purchaseOrderId
          // );
          // purchaseOrders.data.splice(poIdx, 1);
          // this._purchaseOrders.next(purchaseOrders);
          this.reset();
        }),
      );
  }

  removePurchaseOrderRow(purchaseOrderId :number) {
    const purchaseOrders :any = this._purchaseOrders.getValue();
    const poIdx = purchaseOrders.data.findIndex((row) => row.id == purchaseOrderId);
    purchaseOrders.data.splice(poIdx, 1);
    this._purchaseOrders.next(purchaseOrders);
  }

  updateOrderStatus(purchaseOrderId :number, status :any) :Observable<any> {
    return this.http
      .post(`${this.url}/mt_purchase_orders/${purchaseOrderId}/status`, status)
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  tryUpdateOrAddResp(resp) {
    resp.items_summary = this.getItemsTexts(resp);
    const purchaseOrders :any = this._purchaseOrders.getValue();
    if (!purchaseOrders || !purchaseOrders.data) {
      return;
    }
    const poIdx = purchaseOrders.data.findIndex((row) => row.id == resp.id);
    if (poIdx >= 0) {
      purchaseOrders.data[poIdx] = resp;
    } else {
      purchaseOrders.data.splice(0, 0, resp);
    }
    this._purchaseOrders.next(purchaseOrders);
    this.reset();
  }

  deletePurchaseOrderPayment(
    purchaseOrderId :number,
    purchaseOrderPaymentId :number,
  ) :Observable<any> {
    return this.http
      .delete(
        `${this.url}/mt_purchase_orders/${purchaseOrderId}/payments/${purchaseOrderPaymentId}`,
      )
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  addPurchaseOrderPayment(purchaseOrderId :number, payment :any) :Observable<any> {
    return this.http
      .post(`${this.url}/mt_purchase_orders/${purchaseOrderId}/payments`, payment)
      .pipe(
        tap((resp :any) => {
          this.updatePurchaseOrderPayments(purchaseOrderId, resp);
        }),
      );
  }

  deletePurchaseOrderShippingCost(
    purchaseOrderId :number,
    purchaseOrderShippingCostId :number,
  ) :Observable<any> {
    return this.http
      .delete(
        `${this.url}/mt_purchase_orders/${purchaseOrderId}/shipping_costs/${purchaseOrderShippingCostId}`,
      )
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  addPurchaseOrderShippingCost(
    purchaseOrderId :number,
    shippingCost :any,
  ) :Observable<any> {
    return this.http
      .post(
        `${this.url}/mt_purchase_orders/${purchaseOrderId}/shipping_costs`,
        shippingCost,
      )
      .pipe(
        tap((resp :any) => {
          this.updateActivePoShippingCosts(resp);
          this.updatePurchaseOrderShippingCost(purchaseOrderId, resp);
        }),
      );
  }

  updatePurchaseOrderPayments(purchaseOrderId, resp) :void {
    const purchaseOrders :any = this._purchaseOrders.getValue();
    if (!purchaseOrders || !purchaseOrders.data) {
      return;
    }
    const poIdx = purchaseOrders.data.findIndex(
      (row) => row.id === purchaseOrderId,
    );
    if (poIdx >= 0) {
      purchaseOrders.data[poIdx].total_paid = resp.reduce(
        (accumulator, poPayment) => {
          return accumulator + poPayment.paid_amount;
        },
        0,
      );
      purchaseOrders.data[poIdx].purchase_order_payments = resp;
    }
    this._purchaseOrders.next(purchaseOrders);
    this.reset();
  }

  updatePurchaseOrderShippingCost(purchaseOrderId, resp) :void {
    const purchaseOrders :any = this._purchaseOrders.getValue();
    if (!purchaseOrders || !purchaseOrders.data) {
      return;
    }
    const poIdx = purchaseOrders.data.findIndex(
      (row) => row.id === purchaseOrderId,
    );
    if (poIdx >= 0) {
      purchaseOrders.data[poIdx].purchase_order_shipping_costs = resp;
    }
    this._purchaseOrders.next(purchaseOrders);
    this.reset();
  }

  reset() {
    this.totalCost = 0;
    this.itemCount = 0;
    this.totalQuantity = 0;
    this._poItems.next([]);
    this._poManItems.next([]);
  }

  getItems() {
    const items :any[] = this._poItems
      .getValue()
      .concat(this._poManItems.getValue());

    return items;
  }

  addItem(item :any) {
    const items :any[] = this._poItems.getValue();
    items.push(item);
    this.itemCount += 1;
    this.totalQuantity += item.quantity;
    this.totalCost += item.line_total;
    this.totalDownpayment += item.downpayment_amount;
    this._poItems.next(items);
  }
  removeItem(idx :number) {
    const items :any[] = this._poItems.getValue();
    const item = items[idx];
    items.splice(idx, 1);
    this.itemCount -= 1;
    this.totalQuantity -= item.quantity;
    this.totalCost -= item.line_total;
    this.totalDownpayment -= item.downpayment_amount;
    this._poItems.next(items);
  }

  addManItem(item :any) {
    const items :any[] = this._poManItems.getValue();
    items.push(item);
    this.itemCount += 1;
    this.totalQuantity += item.quantity;
    this.totalCost += item.line_total;
    this.totalDownpayment += item.downpayment_amount;
    this._poManItems.next(items);
  }

  updateManItem(upItem :any, idx :number) {
    const manitems :any[] = this._poManItems.getValue();
    const items :any[] = this._poItems.getValue();
    manitems[idx] = upItem;
    this.totalQuantity = 0;
    this.totalCost = 0;
    this.totalDownpayment = 0;

    manitems.forEach((e) => {
      this.totalQuantity += e.quantity;
      this.totalCost += e.line_total;
      this.totalDownpayment += e.downpayment_amount;
    });

    items.forEach((e) => {
      this.totalQuantity += e.quantity;
      this.totalCost += e.line_total;
      this.totalDownpayment += e.downpayment_amount;
    });
    this._poManItems.next(manitems);
    this._poItems.next(items);
  }

  removeManItem(idx :number) {
    const items :any[] = this._poManItems.getValue();
    const item = items[idx];
    items.splice(idx, 1);
    this.itemCount -= 1;
    this.totalQuantity -= item.quantity;
    this.totalCost -= item.line_total;
    this.totalDownpayment -= item.downpayment_amount;
    this._poManItems.next(items);
  }

  async toggleStarred(purchaseOrder :any) {
    const params :any = { starred: 0 };
    if (parseInt(purchaseOrder.starred) == 0) {
      const { value: notes } = await Swal.fire({
        title: 'Order to ' + purchaseOrder.factory.factory_name,
        input: 'textarea',
        inputPlaceholder: 'Type your notes here...',
        inputAttributes: {
          'aria-label': 'Type your notes here',
        },
        showCancelButton: true,
      });
      if (notes !== undefined) {
        params.starred_notes = notes;
        params.starred = 1;
      } else {
        params.starred_notes = '';
      }
    }

    return this.http
      .post(`${this.url}/mt_purchase_orders/${purchaseOrder.id}/starred`, params)
      .subscribe((resp :any) => {
        const record = this._purchaseOrders.getValue();

        if (record && record.data && record.data.length > 0) {
          const idx = record.data.findIndex((row) => {
            return row.id == purchaseOrder.id;
          });

          if (idx || idx >= 0) {
            record.data[idx].starred = params.starred;
            record.data[idx].starred_notes = params.starred_notes;
          }
          this._purchaseOrders.next(record);
        }
      });
  }

  getChartSummary() :Observable<any> {
    return this.http.get(`${this.url}/mt_purchase_orders/summary_chart`, {});
  }

  getChartByFactory() :Observable<any> {
    return this.http.get(`${this.url}/mt_purchase_orders/summary_by_factory`, {});
  }

  getChartByArrivalDate() :Observable<any> {
    return this.http.get(
      `${this.url}/mt_purchase_orders/summary_by_arrival_date`,
      {},
    );
  }

  downloadTemplate() :void {
    const fileName :string = 'purchase-order-upload-template';

    // Records sheet
    const jsonData :any = [
      {
        'SKU Product': 'SKU1',
        'Source SKU': 'sample',
        Quantity: 5,
        'Unit Cost': 1500,
        Downpayment: 0,
      },
    ];

    if (
      this.featureToggleService.isActive(
        'red_DV-2659_fifo_landed_cost_multiple_shipments',
      )
    ) {
      jsonData.forEach((item) => {
        delete item.Downpayment;
      });
    }
    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    ['A1', 'B1'].map((k) => {
      recordsSheet[k].s = { font: { bold: true } };
    });

    recordsSheet['!cols'] = [{ wch: 50 }];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  private saveExcelFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  uploadPOLineItems(fd :FormData) {
    return this.http.post(
      `${this.url}/mt_purchase_orders/upload-po-line-items`,
      fd,
    );
  }

  getReceivableQty(purchaseOrderId :number) :Observable<any> {
    return this.http.get(
      `${this.url}/mt_purchase_orders/${purchaseOrderId}/receivable-qty`,
      {},
    );
  }

  getPoShippingCosts(purchaseOrderId :number) :Subscription {
    return this.getPurchaseOrderByIdAsync(purchaseOrderId)
      .pipe(
        map((res) => {
          return res?.purchase_order_shipping_costs || [];
        }),
      )
      .subscribe((shippingCosts) => {
        this.updateActivePoShippingCosts(shippingCosts);
      });
  }

  updateActivePoShippingCosts(shippingCosts :any) :void {
    this._activePoShippingCosts.next(shippingCosts);
  }

  getPoList(params) :Observable<any> {
    return this.http
      .get(`${this.url}/mt_purchase_orders`, {
        params: params,
      })
      .pipe(
        tap((resp :any) => {
          this.gettingRecords = false;
          if (resp.data) {
            resp.data.forEach((row :any) => {
              row.items_summary = this.getItemsTexts(row);

              // check if in-transit
              if (row.order_status_id === 50) {
                const transitStatus :any = row.purchase_order_status.find(
                  (stat :any) => stat.order_status_id === 50,
                );
                if (transitStatus) {
                  try {
                    transitStatus.json = JSON.parse(transitStatus.json);
                  } catch (e) {}
                  row.transitStatus = transitStatus;
                }
              }
            });
            this._purchaseOrders.next(resp);
          }
        }),
        takeUntil(this.onDestroy$),
      );
  }

  getPickingItemsBySalesOrderId(salesOrderId :number) :Observable<any> {
    return this.http.get<GetPickingItemsResponse>(
      `${this.url}/sales/shipstation_sales/${salesOrderId}/picking_items`,
    );
  }

  getSupplierPurchaseOrders(params ?:{
    [param :string] :string | string[] | boolean;
  }) :Subscription {
    this._loading.next(true);

    return this.http
      .get(`${environment.url}/mt_purchase_orders`, {
        params,
      })
      .subscribe(
        (response :any) => {
          this._loading.next(false);
          if (params?.exact) {
            this._exactPurchaseOrders.next(response.data);
          } else {
            this._similarPurchaseOrders.next(response.data);
          }
        },
        (response :any) => {
          this._loading.next(false);
          throwError(response.error);
        },
      );
  }

  getPickingItemsBySalesOrderIds(params ?:{
    [param :string] :string | boolean;
  }) :Subscription {
    this._loadingPicklistItems.next(true);

    return this.http
      .get(`${environment.url}/sales/shipstation_sales/picking_items`, {
        params,
      })
      .subscribe(
        (response :any) => {
          this._loadingPicklistItems.next(false);
          this._pickingItemsFromOrderIds.next(response);
        },
        (response :any) => {
          this._loadingPicklistItems.next(false);
          throwError(response.error);
        },
      );
  }

  updateApprovalStatus(
    approvalId :number,
    params ?:{ [param :string] :string | boolean },
  ) :Observable<any> {
    return this.http.put(`${this.urlV2}/approvals/${approvalId}/status`, params);
  }
}
